import React, { useContext, useEffect, useMemo, useState } from 'react'
import { PTSControlContext } from './PTSControl'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
  alpha,
} from '@mui/material'
import {
  AltRoute,
  LocationOn,
  People,
  Route,
  ArrowDropDown,
  AccessTime,
  ChatBubble,
  LocalHospitalRounded,
} from '@mui/icons-material'
import API from '../../../services/API'
import ErrorModal from '../../../components/Modals/Error'
import AmbulanceIcon from '../../../assets/icons/Ambulance'
import {
  DutyVehicleImage,
  getStatusColorStyles,
  deriveDutyDisplayStatus,
} from './DutyCard'
import RouteInspector from './components/RouteInspector'
import Map from './components/Map'
import PTSDutyChat from '../components/PTSDutyChat'
import AssignVehicleToDutyDialog from './AssignVehicleToDutyDialog'
import useIsMobile from '../../../helpers/useIsMobile'
import moment from 'moment'

const getShiftAssigneeName = (shift) => {
  if (shift?.assignedTo) {
    return `${shift.assignedTo.userAccount?.firstName ?? ''} ${
      shift.assignedTo.userAccount?.lastName ?? ''
    }`.trim()
  }

  if (shift?.assignedSubcontractor) {
    return `${shift.assignedSubcontractor.userAccount?.firstName ?? ''} ${
      shift.assignedSubcontractor.userAccount?.lastName ?? ''
    }`.trim()
  }

  if (shift?.assignedToSubcontractor) return 'Subcontractor'

  return 'Unassigned'
}

const toDateTimeLocalValue = (value) =>
  value ? moment(value).format('YYYY-MM-DDTHH:mm') : ''

const StatPill = ({ icon, label, value, color }) => {
  return (
    <Stack
      direction="row"
      spacing={0.75}
      alignItems="center"
      sx={{
        px: 1.25,
        py: 0.8,
        borderRadius: 2,
        backgroundColor: 'rgba(15,23,42,0.78)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255,255,255,0.08)',
        boxShadow: '0 8px 18px rgba(0,0,0,0.2)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          color: color || 'rgba(255,255,255,0.8)',
        }}
      >
        {icon}
      </Box>
      <Typography
        sx={{
          fontSize: '0.8rem',
          color: 'rgba(255,255,255,0.72)',
          fontWeight: 600,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: '0.9rem',
          color: '#fff',
          fontWeight: 800,
        }}
      >
        {value}
      </Typography>
    </Stack>
  )
}

export const FloatingPanel = ({ children, sx = {} }) => (
  <Box
    sx={{
      borderRadius: 3,
      background: 'rgba(15,23,42,0.82)',
      backdropFilter: 'blur(14px)',
      border: '1px solid rgba(255,255,255,0.08)',
      boxShadow: `
        0 10px 30px rgba(0,0,0,0.28),
        inset 0 1px 0 rgba(255,255,255,0.04)
      `,
      ...sx,
    }}
  >
    {children}
  </Box>
)

const CentrePanel = ({ duties = [] }) => {
  const {
    selectedDuty,
    setSelectedDuty,
    selectedRoute,
    setSelectedRoute,
    refreshControl,
    markDutyChatRead,
  } = useContext(PTSControlContext)
  const isPhone = useIsMobile('sm')

  const [loading, setLoading] = useState(false)
  const [fetchError, setFetchError] = useState(false)
  const [selectedDutyInfo, setSelectedDutyInfo] = useState(null)
  const [chatOpen, setChatOpen] = useState(false)
  const [vehicleDialogOpen, setVehicleDialogOpen] = useState(false)
  const [endDutyPrompt, setEndDutyPrompt] = useState(null)
  const [shiftEndPrompt, setShiftEndPrompt] = useState(null)
  const [endDutyError, setEndDutyError] = useState(null)
  const [endingDuty, setEndingDuty] = useState(false)
  const [vehicleImageErrored, setVehicleImageErrored] = useState(false)

  useEffect(() => {
    setVehicleImageErrored(false)
  }, [selectedDuty?.vehicle?.image])

  useEffect(() => {
    if (!selectedDuty?._id) {
      setSelectedDutyInfo(null)
      setLoading(false)
      return
    }

    setLoading(true)

    API.get(`/pts/controlroom/dutyinfo/${selectedDuty._id}`)
      .then((res) => {
        if (res.status === 200) {
          setSelectedDutyInfo(res.data)
        } else {
          if (res.status === 401 && res?.data?.errorCode === 'noUserSession') {
            window.location.reload()
          }
          setFetchError(true)
        }
      })
      .catch((e) => {
        console.error(e)
        setFetchError(true)
      })
      .finally(() => setLoading(false))
  }, [selectedDuty])

  const endDuty = async ({
    unfinishedAction,
    shiftEndReviewConfirmed = false,
    shiftEndAdjustments,
  } = {}) => {
    if (!selectedDutyInfo?._id) return
    setEndingDuty(true)
    setEndDutyError(null)

    try {
      const res = await API.post(`/pts/duties/${selectedDutyInfo._id}/end`, {
        unfinishedAction,
        shiftEndReviewConfirmed,
        shiftEndAdjustments,
      })

      if (res.status === 200) {
        setEndDutyPrompt(null)
        setShiftEndPrompt(null)
        setSelectedDutyInfo(null)
        setSelectedDuty(null)
        refreshControl?.()
        return
      }

      if (res.status === 409 && res.data?.errorCode === 'unfinishedJourneys') {
        setEndDutyPrompt({
          openJourneyCount: res.data.openJourneyCount || 0,
        })
        return
      }

      if (res.status === 409 && res.data?.errorCode === 'shiftEndReviewRequired') {
        setEndDutyPrompt(null)
        setShiftEndPrompt({
          unfinishedAction,
          bufferMinutes: res.data.bufferMinutes,
          shifts: (res.data.shifts ?? []).map((shift) => ({
            ...shift,
            actualFinishTime: toDateTimeLocalValue(shift.defaultFinishTime),
            overtimeReason: '',
          })),
        })
        return
      }

      setEndDutyError(res.data?.error || 'Unable to end duty.')
    } catch (e) {
      setEndDutyError('Unable to end duty.')
    } finally {
      setEndingDuty(false)
    }
  }

  const allJourneys = useMemo(
    () =>
      (selectedDutyInfo?.routes ?? []).flatMap(
        (route) => route?.journeys ?? [],
      ),
    [selectedDutyInfo],
  )

  // Derive the live status the same way the duty cards do — the stored
  // duty.status only ever holds Off Duty / En Route / Completed, so reading
  // it raw showed OFF DUTY for every clocked-in crew with no active journey
  const patientsOnBoardCount = useMemo(
    () =>
      allJourneys.filter((journey) =>
        ['Patient Onboard', 'Transporting'].includes(journey?.status),
      ).length,
    [allJourneys],
  )
  const statusStyles = getStatusColorStyles(
    selectedDutyInfo
      ? deriveDutyDisplayStatus({
          duty: selectedDutyInfo,
          allJourneys,
          patientsOnBoardCount,
        })
      : selectedDutyInfo?.status,
  )

  const uniquePatientCount = useMemo(() => {
    return new Set(
      allJourneys
        .map((j) => j?.booking?.patient?._id || j?.patient)
        .filter(Boolean),
    ).size
  }, [allJourneys])

  // dutyinfo returns populated crewShifts but not the aggregation's count
  // fields — derive them so the unfilled-shift warning actually fires
  const crewShiftCount =
    selectedDutyInfo?.crewShiftCount ??
    selectedDutyInfo?.crewShifts?.length ??
    0
  const filledShiftCount =
    selectedDutyInfo?.filledShiftCount ??
    (selectedDutyInfo?.crewShifts || []).filter(
      (shift) => shift?.assignedTo || shift?.assignedSubcontractor,
    ).length
  const hasEmptyShifts = filledShiftCount < crewShiftCount

  const totalOpenJourneys = useMemo(() => {
    return duties.reduce((sum, duty) => sum + (duty?.openJourneyCount ?? 0), 0)
  }, [duties])

  const totalIncompleteRoutes = useMemo(() => {
    return duties.reduce(
      (sum, duty) => sum + (duty?.incompleteRouteCount ?? 0),
      0,
    )
  }, [duties])

  return (
    <Paper
      sx={{
        height: '100%',
        minHeight: { xs: 320, sm: 520 },
        borderRadius: 3,
        overflow: 'hidden',
        position: 'relative',
        background: 'linear-gradient(180deg, #0F172A 0%, #0B1220 100%)',
        border: '1px solid rgba(255,255,255,0.06)',
        boxShadow: `
          0 10px 30px rgba(0,0,0,0.35),
          inset 0 1px 0 rgba(255,255,255,0.04)
        `,
      }}
    >
      <ErrorModal
        open={fetchError}
        onClose={() => window.location.reload()}
        message="Unable to Fetch Duty Information. Please contact AmbuNet support ASAP via chat or by calling 0330 057 0620."
      />

      {loading && (
        <Box
          sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 20 }}
        >
          <LinearProgress />
        </Box>
      )}

      {/* Always-visible map area */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(circle at top, rgba(30,41,59,0.72) 0%, rgba(15,23,42,1) 55%)',
        }}
      >
        {/* Placeholder grid */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)
            `,
            backgroundSize: '32px 32px',
            opacity: 0.25,
          }}
        />

        {/* Fake road glow / visual interest */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(circle at 20% 35%, rgba(59,130,246,0.10) 0%, transparent 22%), radial-gradient(circle at 70% 55%, rgba(245,158,11,0.08) 0%, transparent 18%), radial-gradient(circle at 55% 20%, rgba(34,197,94,0.08) 0%, transparent 20%)',
          }}
        />

        {/* Replace this whole block with MapLibre */}
        {/* <Stack
          alignItems="center"
          justifyContent="center"
          sx={{
            position: 'absolute',
            inset: 0,
            textAlign: 'center',
            px: 3,
          }}
          spacing={1}
        > */}
        <Map
          duties={duties}
          selectedDuty={selectedDuty}
          selectedDutyInfo={selectedDutyInfo}
          setSelectedDuty={setSelectedDuty}
          selectedRoute={selectedRoute}
          setSelectedRoute={setSelectedRoute}
        />
        {/* </Stack> */}
      </Box>

      {/* Selected duty floating header */}

      {selectedDutyInfo && (
        <Box
          sx={{
            position: 'absolute',
            top: { xs: 8, sm: 16 },
            left: { xs: 8, sm: 16 },
            right: { xs: 8, sm: 16 },
            zIndex: 10,
          }}
        >
          <FloatingPanel sx={{ p: { xs: 1.25, sm: 2 } }}>
            <Stack spacing={1.25}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
                spacing={1.25}
              >
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Box
                    sx={{
                      width: 46,
                      height: 46,
                      borderRadius: 2,
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(59,130,246,0.12)',
                      border: '1px solid rgba(59,130,246,0.24)',
                      position: 'relative',
                    }}
                  >
                    {selectedDuty?.vehicle?.image && !vehicleImageErrored ? (
                      <Box
                        component="img"
                        src={selectedDuty.vehicle.image}
                        alt={selectedDuty?.vehicle?.callSign || 'Vehicle'}
                        onError={() => setVehicleImageErrored(true)}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block',
                          filter: 'saturate(1.02) contrast(1.02)',
                        }}
                      />
                    ) : (
                      <Stack
                        alignItems="center"
                        justifyContent="center"
                        sx={{ width: '100%', height: '100%' }}
                        color="white"
                      >
                        <AmbulanceIcon
                          style={{
                            fontSize: 34,
                          }}
                        />
                      </Stack>
                    )}

                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        background:
                          'linear-gradient(90deg, rgba(2,6,23,0.08) 0%, rgba(2,6,23,0.18) 38%, rgba(2,6,23,0.42) 100%)',
                      }}
                    />
                  </Box>

                  <Box>
                    <Typography
                      sx={{
                        color: selectedDutyInfo?.vehicle?.callSign
                          ? '#F8FAFC'
                          : '#FDE68A',
                        fontWeight: 800,
                        fontSize: '1.5rem',
                        lineHeight: 1.1,
                      }}
                    >
                      {selectedDutyInfo?.vehicle?.callSign ?? 'No Vehicle'}
                    </Typography>
                    <Typography
                      sx={{
                        color: 'rgba(226,232,240,0.68)',
                        fontSize: '0.9rem',
                      }}
                    >
                      {selectedDutyInfo?.baseLocation?.name ??
                        'No base location'}
                    </Typography>
                  </Box>
                </Stack>

                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                  rowGap={1}
                  useFlexGap
                >
                  <Button
                    size="small"
                    variant={
                      selectedDutyInfo?.vehicle?.callSign
                        ? 'outlined'
                        : 'contained'
                    }
                    onClick={() => setVehicleDialogOpen(true)}
                    sx={
                      selectedDutyInfo?.vehicle?.callSign
                        ? {
                            borderRadius: 2,
                            color: '#E2E8F0',
                            borderColor: 'rgba(226,232,240,0.32)',
                            fontWeight: 900,
                            textTransform: 'none',
                            '&:hover': {
                              borderColor: 'rgba(226,232,240,0.6)',
                              bgcolor: 'rgba(255,255,255,0.06)',
                            },
                          }
                        : {
                            borderRadius: 2,
                            bgcolor: 'rgba(245,158,11,0.92)',
                            color: '#1F2937',
                            fontWeight: 900,
                            textTransform: 'none',
                            '&:hover': { bgcolor: 'rgba(217,119,6,0.98)' },
                          }
                    }
                  >
                    {selectedDutyInfo?.vehicle?.callSign
                      ? 'Change Vehicle'
                      : 'Assign Vehicle'}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      window.open(
                        `/pts/planning?date=${moment(
                          selectedDutyInfo?.start,
                        ).format('YYYYMMDD')}`,
                      )
                    }
                    sx={{
                      borderRadius: 2,
                      color: '#E2E8F0',
                      borderColor: 'rgba(226,232,240,0.32)',
                      fontWeight: 900,
                      textTransform: 'none',
                      '&:hover': {
                        borderColor: 'rgba(226,232,240,0.6)',
                        bgcolor: 'rgba(255,255,255,0.06)',
                      },
                    }}
                  >
                    Planning
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<ChatBubble />}
                    onClick={() => {
                      markDutyChatRead?.(
                        selectedDuty?._id,
                        selectedDuty?.crewChatMessageCount || 0,
                      )
                      setChatOpen(true)
                    }}
                    sx={{
                      borderRadius: 2,
                      bgcolor: 'rgba(20,184,166,0.92)',
                      color: '#fff',
                      fontWeight: 900,
                      textTransform: 'none',
                      '&:hover': { bgcolor: 'rgba(15,118,110,0.98)' },
                    }}
                  >
                    Chat
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    disabled={endingDuty}
                    onClick={() => endDuty()}
                    sx={{
                      borderRadius: 2,
                      fontWeight: 900,
                      textTransform: 'none',
                    }}
                  >
                    End Duty
                  </Button>
                  <Chip
                    label={(selectedDutyInfo?.status || 'Unknown').toUpperCase()}
                    sx={{
                      height: 32,
                      fontWeight: 800,
                      letterSpacing: 0.3,
                      color: statusStyles.color,
                      backgroundColor: statusStyles.bg,
                      border: `1px solid ${statusStyles.border}`,
                      borderRadius: 2,
                      '& .MuiChip-label': {
                        px: 1.2,
                      },
                    }}
                  />
                </Stack>
              </Stack>

              <Stack
                direction={{ xs: 'column', lg: 'row' }}
                justifyContent="space-between"
                spacing={1.25}
              >
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  gap={0.75}
                  alignItems="center"
                >
                  <People
                    sx={{ fontSize: 18, color: 'rgba(255,255,255,0.72)' }}
                  />

                  {(selectedDutyInfo?.crewShifts ?? []).length ? (
                    selectedDutyInfo.crewShifts.map((s, index) => {
                      const name = getShiftAssigneeName(s)
                      const assigned = name !== 'Unassigned'

                      return (
                        <Chip
                          key={s?._id ?? index}
                          label={name}
                          size="small"
                          sx={{
                            borderRadius: 2,
                            height: 24,
                            fontWeight: 700,
                            color: assigned ? '#E2E8F0' : '#FDE68A',
                            backgroundColor: assigned
                              ? 'rgba(255,255,255,0.07)'
                              : 'rgba(245,158,11,0.18)',
                            border: assigned
                              ? '1px solid rgba(255,255,255,0.08)'
                              : '1px solid rgba(253,230,138,0.24)',
                          }}
                        />
                      )
                    })
                  ) : (
                    <Chip
                      label="No Crew"
                      size="small"
                      sx={{
                        borderRadius: 2,
                        height: 24,
                        fontWeight: 700,
                        color: '#FDE68A',
                        backgroundColor: 'rgba(245,158,11,0.18)',
                        border: '1px solid rgba(253,230,138,0.24)',
                      }}
                    />
                  )}
                </Stack>

                <Stack direction="row" flexWrap="wrap" gap={1}>
                  <StatPill
                    icon={<AltRoute sx={{ fontSize: 16 }} />}
                    label="Open"
                    value={selectedDutyInfo?.openJourneyCount ?? 0}
                    color="#86EFAC"
                  />
                  <StatPill
                    icon={<Route sx={{ fontSize: 16 }} />}
                    label="Routes"
                    value={selectedDutyInfo?.incompleteRouteCount ?? 0}
                    color="#FCD34D"
                  />
                  <StatPill
                    icon={<People sx={{ fontSize: 16 }} />}
                    label="Patients"
                    value={uniquePatientCount}
                    color="#E2E8F0"
                  />
                </Stack>
              </Stack>

              {hasEmptyShifts && (
                <Typography
                  sx={{
                    color: '#FDE68A',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                  }}
                >
                  {crewShiftCount - filledShiftCount} unassigned shift
                  {crewShiftCount - filledShiftCount !== 1 ? 's' : ''}
                </Typography>
              )}
            </Stack>
          </FloatingPanel>
        </Box>
      )}

      {selectedDutyInfo && (
        <Box
          sx={{
            // Desktop: a side column on the right of the map. Phone: a bottom
            // sheet spanning the width so the map stays usable above it.
            position: 'absolute',
            top: { xs: 'auto', sm: 160 },
            left: { xs: 8, sm: 'auto' },
            right: { xs: 8, sm: 16 },
            bottom: { xs: 8, sm: 16 },
            height: { xs: '42%', sm: 'auto' },
            zIndex: 10,
            display: 'flex',
            alignItems: 'stretch',
            minHeight: 0,
          }}
        >
          <RouteInspector routes={selectedDutyInfo?.routes ?? []} />
        </Box>
      )}
      <PTSDutyChat
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        dutyID={selectedDutyInfo?._id}
        title={`${selectedDutyInfo?.vehicle?.callSign || 'PTS'} chat`}
      />
      <AssignVehicleToDutyDialog
        open={vehicleDialogOpen}
        duty={selectedDutyInfo}
        onClose={() => setVehicleDialogOpen(false)}
        onAssigned={() => refreshControl?.()}
      />
      <Dialog
        open={!!shiftEndPrompt}
        onClose={() => setShiftEndPrompt(null)}
        fullWidth
        maxWidth="sm"
        fullScreen={isPhone}
      >
        <DialogTitle>Review crew shift end times</DialogTitle>
        <DialogContent>
          <Stack gap={2} sx={{ pt: 1 }}>
            <Typography>
              Ending this duty will also end active crew shifts. Some crew
              shifts are outside the clock-out buffer, so record the actual
              finish time for each staff member before ending the duty.
            </Typography>
            {(shiftEndPrompt?.shifts ?? []).map((shift, index) => (
              <Stack
                key={shift._id}
                gap={1}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Stack direction="row" justifyContent="space-between" gap={1}>
                  <Typography fontWeight={800}>
                    {shift.staffName || 'Crew member'}
                  </Typography>
                  <Chip
                    size="small"
                    color={shift.mode === 'overtime' ? 'warning' : 'info'}
                    label={
                      shift.mode === 'overtime'
                        ? `Beyond ${shiftEndPrompt?.bufferMinutes?.late ?? 15} min overtime buffer`
                        : `Before ${shiftEndPrompt?.bufferMinutes?.early ?? 15} min early finish buffer`
                    }
                    sx={{ borderRadius: 1 }}
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Planned: {moment(shift.start).format('HH:mm')} -{' '}
                  {moment(shift.end).format('HH:mm')}
                </Typography>
                <TextField
                  label="Actual finish time"
                  type="datetime-local"
                  value={shift.actualFinishTime}
                  onChange={(e) =>
                    setShiftEndPrompt((prev) => ({
                      ...prev,
                      shifts: prev.shifts.map((item, itemIndex) =>
                        itemIndex === index
                          ? { ...item, actualFinishTime: e.target.value }
                          : item,
                      ),
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  required
                />
                {shift.mode === 'overtime' && (
                  <TextField
                    label="Overtime reason"
                    value={shift.overtimeReason}
                    onChange={(e) =>
                      setShiftEndPrompt((prev) => ({
                        ...prev,
                        shifts: prev.shifts.map((item, itemIndex) =>
                          itemIndex === index
                            ? { ...item, overtimeReason: e.target.value }
                            : item,
                        ),
                      }))
                    }
                    fullWidth
                    required
                    multiline
                    minRows={2}
                  />
                )}
              </Stack>
            ))}
            {endDutyError && (
              <Typography color="error">{endDutyError}</Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShiftEndPrompt(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={
              endingDuty ||
              (shiftEndPrompt?.shifts ?? []).some(
                (shift) =>
                  !shift.actualFinishTime ||
                  (shift.mode === 'overtime' && !shift.overtimeReason),
              )
            }
            onClick={() =>
              endDuty({
                unfinishedAction: shiftEndPrompt?.unfinishedAction,
                shiftEndReviewConfirmed: true,
                shiftEndAdjustments: (shiftEndPrompt?.shifts ?? []).map(
                  (shift) => ({
                    shiftID: shift._id,
                    actualFinishTime: moment(shift.actualFinishTime).toISOString(),
                    overtimeReason: shift.overtimeReason,
                  }),
                ),
              })
            }
          >
            End duty and update shifts
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!endDutyPrompt}
        onClose={() => setEndDutyPrompt(null)}
        fullScreen={isPhone}
      >
        <DialogTitle>Unfinished journeys</DialogTitle>
        <DialogContent>
          <Typography>
            This duty has {endDutyPrompt?.openJourneyCount || 0} unfinished
            journey
            {(endDutyPrompt?.openJourneyCount || 0) === 1 ? '' : 's'}. You can
            cancel ending the duty, or move the unfinished journeys back to
            unassigned so they can be replanned.
          </Typography>
          {endDutyError && (
            <Typography color="error" sx={{ mt: 1 }}>
              {endDutyError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEndDutyPrompt(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={endingDuty}
            onClick={() =>
              endDuty({ unfinishedAction: 'moveToUnassigned' })
            }
          >
            Move to unassigned and end duty
          </Button>
        </DialogActions>
      </Dialog>
      {endDutyError && !endDutyPrompt && !shiftEndPrompt && (
        <ErrorModal
          open={!!endDutyError}
          onClose={() => setEndDutyError(null)}
          message={endDutyError}
        />
      )}
    </Paper>
  )
}

export default CentrePanel
